import React, { useMemo } from 'react';
import { Typography, Tag, Image, Tooltip, Button } from 'antd';
import { LuExternalLink } from 'react-icons/lu';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../../components/common/CrudPage';
import postService from '../../../services/lichvietweb/postServiceRequest';
import blogService from '../../../services/lichvietweb/blogServiceRequest';
import { CkeditorHelper } from '../../../utils/CkeditorHelper';

const { Text } = Typography;

const PostPreview = ({ data }) => {
  const formattedContent = useMemo(() => {
    const content = data?.content;
    if (!content) return '';

    // Gán ID cho các thẻ heading nếu chưa có
    let contentWithIds = CkeditorHelper.setContentHeadingId(content);

    // Lấy nội dung mục lục
    let tocListHtml = CkeditorHelper.getTableOfContents(contentWithIds);

    let finalTocHtml = tocListHtml || '';

    // Chèn mục lục vào vị trí [[toc]] và xử lý iframe video
    let processedContent = CkeditorHelper.insertNewTableOfContents(contentWithIds, finalTocHtml);
    return CkeditorHelper.insertIframeMedia(processedContent);
  }, [data?.content]);

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white">
      <h1 className="text-3xl font-bold mb-4 text-gray-900">{data.title}</h1>
      {data.subtitle && (
        <div className="text-lg text-gray-600 mb-6 font-medium italic border-l-4 border-gray-300 pl-4">{data.subtitle}</div>
      )}

      <div
        className="prose prose-lg max-w-none ck-content"
        dangerouslySetInnerHTML={{ __html: formattedContent }}
        style={{ lineHeight: '1.8', fontSize: '18px' }}
      />
    </div>
  );
};

const QuanLyBaiViet = () => {
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => blogService.search({ limit: 1000 }),
  });

  const categoryOptions = useMemo(() => {
    const list = categoriesData?.data?.data || categoriesData?.data || categoriesData?.list || [];
    return Array.isArray(list)
      ? list.map((cat) => ({ label: cat.name, value: Number(cat.id) }))
      : [];
  }, [categoriesData]);

  const filterFields = useMemo(() => [
    {
      label: 'Trạng thái',
      value: 'status',
      options: [
        { label: 'Tất cả trạng thái', value: '' },
        { label: 'Đang hoạt động', value: '1' },
        { label: 'Đang bị khóa', value: '0' },
      ],
    },
    {
      label: 'Nổi bật',
      value: 'is_hot',
      options: [
        { label: 'Tất cả bài viết', value: '' },
        { label: 'Nổi bật', value: '1' },
        { label: 'Bình thường', value: '0' },
      ],
    },
  ], []);

  const postFields = useMemo(
    () => [
      { type: 'divider', label: 'Thông tin cơ bản' },
      {
        name: 'title',
        label: 'Tiêu đề bài viết',
        type: 'input',
        rules: [{ required: true }],
        placeholder: 'Nhập tiêu đề...',
      },
      {
        name: 'slug',
        label: 'Đường dẫn (Slug)',
        type: 'input',
        rules: [{ required: true }],
        placeholder: 'vi-du-duong-dan-bai-viet',
        md: 12,
      },
      {
        name: 'date',
        label: 'Ngày hiển thị',
        type: 'date',
        md: 12,
        rules: [{ required: true, message: 'Vui lòng chọn ngày hiển thị' }],
        defaultValue: dayjs(),
        submitFormat: 'YYYY-MM-DD HH:mm',
        propsInput: {
          showTime: true,
          format: 'DD/MM/YYYY HH:mm',
        },
      },
      {
        name: 'categories',
        label: 'Danh mục',
        type: 'select',
        options: categoryOptions,
        rules: [{ required: true, message: 'Vui lòng chọn danh mục' }],
        md: 24,
        propsInput: {
          mode: 'multiple',
          showSearch: true,
          optionFilterProp: 'label',
          // popupClassName: 'hide-selected-options',
          filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
        },
      },
      {
        name: 'subtitle',
        label: 'Mô tả ngắn (Subtitle)',
        type: 'textarea',
        rules: [{ required: true }],
        rows: 3,
      },
      { type: 'divider', label: 'Ảnh & SEO' },
      {
        name: 'image',
        label: 'Ảnh đại diện',
        type: 'upload-cdn',
        md: 8,
        uploadType: 'cdn',
      },
      {
        name: 'image_alt',
        label: 'Alt của ảnh',
        type: 'input',
        md: 16,
      },
      {
        name: 'meta_title',
        label: 'Meta Title',
        type: 'input',
      },
      {
        name: 'meta_description',
        label: 'Meta Description',
        type: 'textarea',
        rows: 2,
      },
      {
        name: 'canonical',
        label: 'Canonical URL',
        type: 'input',
        placeholder: 'Ví dụ: https://lichviet.app/tin-tuc/bai-viet-goc',
      },
      {
        name: 'breadcrumb',
        label: 'Breadcrumb Schema (JSON)',
        type: 'textarea',
        rows: 6,
        autoSize: false,
        placeholder: `{
  "@context": "http://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": { "@id": "/", "name": "Trang chủ" }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": { "@id": "/tin-tuc", "name": "Tin Tức" }
    }
  ]
}`,
      },
      {
        name: 'no_index',
        label: 'Trạng thái Index',
        type: 'checkbox',
        labelCheckbox: 'Không Index (Chặn Google Index)',
        md: 8,
      },
      { type: 'divider', label: 'Nội dung bài viết' },
      {
        name: 'content',
        label: 'Nội dung chi tiết',
        type: 'ckeditor',
      },
      { type: 'divider', label: 'Cài đặt khác' },
      {
        name: 'scripts',
        label: 'Article Schema (JSON)',
        type: 'textarea',
        rows: 10,
        autoSize: false,
        placeholder: `[{
  "type": "application/ld+json",
  "name": "SchemaPage",
  "content": {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Tiêu đề bài viết...",
    "description": "Mô tả ngắn...",
    "image": "https://...",
    "author": { "@type": "Person", "name": "Author Name" },
    "datePublished": "2023-01-01"
  }
      }]`,
      },
      {
        name: 'place',
        label: 'Vị trí (Thứ tự)',
        type: 'number',
        md: 8,
      },
      {
        name: 'is_hot',
        label: 'Bài viết nổi bật',
        type: 'select',
        md: 8,
        options: [
          { label: 'Bình thường', value: '0' },
          { label: 'Nổi bật', value: '1' },
        ],
        defaultChecked: '0',
      },
      {
        name: 'status',
        label: 'Trạng thái',
        type: 'select',
        md: 8,
        options: [
          { label: 'Hoạt động', value: 1 },
          { label: 'Khóa', value: 0 },
        ],
        defaultChecked: 1,
      },
    ],
    [categoryOptions]
  );

  const columns = useMemo(() => [
    {
      title: 'ẢNH',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (url) => <Image src={url} width={60} className="rounded-lg object-cover" fallback="https://placehold.co/60x60?text=No+Image" />,
    },
    {
      title: 'TIÊU ĐỀ',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div className="flex flex-col">
          <Text strong className="line-clamp-2">{text}</Text>
          <Text type="secondary" className="text-[12px]">{record.slug}</Text>
        </div>
      ),
    },
    {
      title: 'DANH MỤC',
      dataIndex: 'categories',
      key: 'categories',
      width: 150,
      render: (categories) => (
        <div className="flex flex-wrap gap-1">
          {Array.isArray(categories) &&
            categories.map((cat) => (
              <Tag key={cat.id} color="blue" className="mr-0">
                {cat.name}
              </Tag>
            ))}
        </div>
      ),
    },
    {
      title: 'VỊ TRÍ',
      dataIndex: 'place',
      key: 'place',
      width: 80,
      align: 'center',
    },
    {
      title: 'NỔI BẬT',
      dataIndex: 'is_hot',
      key: 'is_hot',
      width: 100,
      align: 'center',
      render: (hot) => (hot === '1' ? <Tag color="gold">HOT</Tag> : null),
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? 'HOẠT ĐỘNG' : 'KHÓA'}</Tag>,
    },
    {
      title: 'NGÀY HIỂN THỊ',
      dataIndex: 'date',
      key: 'date',
      width: 150,
      render: (date) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
    },
  ], []);

  return (
    <CrudPage
      title="QUẢN LÝ BÀI VIẾT"
      subTitle="Quản lý danh sách các bài viết trên website Lịch Việt"
      itemsName="Bài viết"
      queryKey="postList"
      service={postService}
      columns={columns}
      formFields={postFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm tiêu đề, slug..."
      addButtonLabel="Thêm bài viết"
      formConfig={{ width: 1000 }}
      PreviewComponent={PostPreview}
      mapDataToForm={(record) => ({
        categories: record.categories?.map((c) => Number(c.id)),
        scripts:
          record.scripts && typeof record.scripts === 'object'
            ? JSON.stringify(record.scripts, null, 2)
            : record.scripts,
        breadcrumb:
          record.breadcrumb && typeof record.breadcrumb === 'object'
            ? JSON.stringify(record.breadcrumb, null, 2)
            : record.breadcrumb,
      })}
      mapFormToData={(values) => ({
        scripts: values.scripts ? JSON.parse(values.scripts) : null,
        breadcrumb: values.breadcrumb ? JSON.parse(values.breadcrumb) : null,
      })}
    />
  );
};

export default QuanLyBaiViet;
